import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { ApiError } from '@/api/errors'
import { ErrorState, LoadingState, NotFoundState } from '@/components/feedback/States'
import { PageHeader } from '@/components/ui/PageHeader'
import { bookQuery } from '@/features/library/api'
import { BookForm } from '@/features/library/components/BookForm'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

export function BookFormPage() {
  const { t } = useTranslation('library')
  const { id } = useParams()
  const editing = id !== undefined
  const navigate = useNavigate()
  const detail = useQuery({ ...bookQuery(Number(id)), enabled: editing })
  useDocumentTitle(editing ? 'Edit book' : 'Add book')

  if (editing && detail.isPending) return <LoadingState />
  if (editing && detail.error)
    return detail.error instanceof ApiError && detail.error.status === 404 ? (
      <NotFoundState resource="book" homeHref="/admin/library" />
    ) : (
      <ErrorState onRetry={() => void detail.refetch()} />
    )

  const book = detail.data?.book
  return (
    <>
      <PageHeader
        title={book ? t('Edit “{{title}}”', { title: book.title }) : t('Add a book')}
        description={
          book
            ? book.authors
            : t('Add a title to the catalogue. Copies become available to borrow immediately.')
        }
        breadcrumbs={[
          { label: t('Library'), to: '/admin/library' },
          ...(book ? [{ label: book.title, to: `/admin/library/books/${book.id}` }] : []),
          { label: book ? t('Edit') : t('Add') },
        ]}
        homeHref="/admin"
      />
      <BookForm
        key={book?.id ?? 'new'}
        book={book}
        onSaved={(saved) => void navigate(`/admin/library/books/${saved.book.id}`)}
        onCancel={() => void navigate(book ? `/admin/library/books/${book.id}` : '/admin/library')}
      />
    </>
  )
}
